<template>
  <div class="borrowerAttributeAddress">
    <BorrowerAttributeConstructor
      :questionText="questionText"
      :description="description"
      :descriptionTooltip="descriptionTooltip"
      :tooltipText="tooltipText"
      :errorText="errorText"
      :helpText="helpText"
      :disclaimer="disclaimer">
      <template v-slot:input>
        <div class="d-grid ba-address-container">
          <!-- street -->
          <div class="row">
            <div class="col">
              <div class="form-floating">
                <!--  Using type="search" instead of type="text" on address input field disables address input autofill while still allowing google places autocomplete. autocomplete="nope" does not work in this case since google places autocomplete overrides and sets autocomplete="off" - autocomplete="off" does not work for chrome browser. -->
                <input
                  type="search"
                  ref="autocomplete"
                  class="form-control"
                  :class="{
                    'is-invalid': v$.street?.$error,
                    'disabled': disabled,
                    'disabled-repressed': disabled && repressDisabledStyle,
                  }"
                  v-model="street"
                  :id="selectors.street"
                  :name="selectors.street"
                  :data-cy="selectors.street"
                  :placeholder="labels.street"
                  :disabled="disabled"
                  @blur="v$.street?.$touch"
                  @change="onAutoComplete"
                  @keyup="onChange"
                  @focus="onFocus"
                />
                <label class="type-400" :for="selectors.street">
                  <span>{{ labels.street }}</span>
                </label>
                <span class="invalid-feedback" v-if="v$.street?.$error">
                  <fa-icon :icon="['fal', 'circle-exclamation']"></fa-icon>
                  {{ v$.street?.$errors[0]?.$message }}
                </span>
              </div>
            </div>
          </div>

          <!-- city -->
          <div class="row">
            <div class="col">
              <div class="form-floating">
                <input
                  type="text"
                  ref="city"
                  class="form-control"
                  :class="{
                    'is-invalid': v$.city?.$error,
                    'disabled': disabled,
                    'disabled-repressed': disabled && repressDisabledStyle,
                  }"
                  v-model="city"
                  :disabled="disabled"
                  :id="selectors.city"
                  :name="selectors.city"
                  :data-cy="selectors.city"
                  :placeholder="labels.city"
                  @blur="v$.city?.$touch"
                  @change="onChange"
                  @keyup="onChange"
                />
                <label class="type-400" :for="selectors.city">
                  <span>{{ labels.city }}</span>
                </label>
                <span class="invalid-feedback" v-if="v$.city?.$error">
                  <fa-icon :icon="['fal', 'circle-exclamation']"></fa-icon>
                  {{ v$.city?.$errors[0]?.$message }}
                </span>
              </div>
            </div>
          </div>

          <div class="row marketplace-state-zip">
            <!-- state -->
            <div class="col-12 col-sm-6">
              <div class="form-floating">
                <select
                  class="form-select mb-5 mb-sm-0"
                  :class="{
                    'is-invalid': v$.state?.$error,
                    'label-visible': !!state,
                    'no-value-select': !state,
                    'disabled': disabled,
                    'disabled-repressed': disabled && repressDisabledStyle,
                  }"
                  v-model="state"
                  :id="selectors.state"
                  :name="selectors.state"
                  :data-cy="selectors.state"
                  :disabled="disabled"
                  @blur="v$.state?.$touch"
                  @change="onChange"
                  @keyup="onChange"
                >
                  <option :value="''" disabled selected>{{ labels.state }}</option>
                  <option
                    v-for="(option, i) in options"
                    :value="option.value"
                    :key="i">
                    {{ option.label }}
                  </option>
                </select>
                <label class="type-400" v-if="state" :for="selectors.state">
                  <span>{{ labels.state }}</span>
                </label>
                <span class="invalid-feedback" v-if="v$.state?.$error">
                  <fa-icon :icon="['fal', 'circle-exclamation']"></fa-icon>
                  {{ v$.state?.$errors[0]?.$message }}
                </span>
              </div>
            </div>

            <!-- zip -->
            <div class="col-12 col-sm-6">
              <div class="form-floating">
                <input
                  type="text"
                  class="form-control form-zip required"
                  :class="{
                    'is-invalid': v$.zip?.$error,
                    'disabled': disabled,
                    'disabled-repressed': disabled && repressDisabledStyle,
                  }"
                  inputmode="numeric"
                  masked=true
                  pattern="[0-9]*"
                  v-maska data-maska="#####"
                  v-model="zip"
                  :id="selectors.zip"
                  :name="selectors.zip"
                  :data-cy="selectors.zip"
                  :placeholder="labels.zip"
                  :disabled="disabled"
                  @blur="v$.zip?.$touch"
                  @change="onChange"
                  @keyup="onChange"
                >
                <label class="type-400" :for="selectors.zip">
                  <span>{{ labels.zip }}</span>
                </label>
                <div class="invalid-feedback" v-if="v$.zip?.$error && v$.zip?.$errors[0]?.$message">
                  <fa-icon :icon="['fal', 'circle-exclamation']"></fa-icon>
                  {{ v$.zip?.$errors[0]?.$message }}
                </div>
                <div class="pt-1" v-else-if="v$.zip?.$pending && v$.zip?.$error">
                  <!-- Async Validator Spinner  -->
                  <span class="text-danger spinner-border spinner-border-sm"></span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </template>
    </BorrowerAttributeConstructor>
  </div>
</template>

<script>
import BaseBorrowerAttributeMixin from './BaseBorrowerAttributeMixin'
import startCase from 'lodash/startCase'
import { lsUtils } from '~/libs/ls-utils-service.js'
import { useVuelidate } from '@vuelidate/core'
import { required, minLength, helpers } from '@vuelidate/validators'
const { withAsync, withMessage } = helpers
import { zipValidator } from '~/libs/validators/zipValidator'
import LoadScript, { GOOGLE_MAPS } from '../../libs/script-loader'

export default {
  mixins: [BaseBorrowerAttributeMixin],
  props: {
    disabled: Boolean,
    repressDisabledStyle: Boolean
  },
  emits: ['invalidity','answer','focus'],
  setup: () => ({ v$: useVuelidate() }),
  data () {
    const components = ['street', 'street2', 'city', 'state', 'zip'];
    return {
      street: this.question.value.street,
      city: this.question.value.city,
      state: this.question.value.state || '',
      zip: this.question.value.zip,
      selectors: components.reduce((selectors, component) => {
        selectors[component] = `ba-${this.question.alias}-${component}`; // Example: ba-businessAddress-street
        return selectors
      }, {}),
      labels: components.reduce((labels, component) => {
        if (this.question.excludeAliasFromPlaceholders) {
          labels[component] = startCase(component).replace(' Address', '')
        } else {
          labels[component] = startCase(`${this.question.alias} ${component}`).replace(' Address', '')
        }
        return labels
      }, {}),
      options: lsUtils.getUsStateOptions(),
      autocomplete: null,
    }
  },
  validations () {
    return {
      street: {
        required: withMessage('Enter a street address', required),
        minLength: minLength(3),
      },
      city: {
        required: withMessage('Enter a city', required),
        minLength: minLength(3)
      },
      state: {
        required: withMessage('Select a state', required),
        minLength: minLength(2)
      },
      zip: {
        required: withMessage('Enter a zip code', required),
        minLength: withMessage('Zip codes must be 5 digits', minLength(5)),
        zipExists: withAsync(
          withMessage('Enter a valid zip code', zipValidator(this.$axios))
        )
      },
      $validationGroups: {
        fullAddress: ['city', 'state', 'zip']
      }
    }
  },
  created () {
    this.$watch('invalid', (invalid) => {
      this.$emit('invalidity', invalid)
    }, {immediate: true})
  },
  async mounted () {
    try {
      await LoadScript(GOOGLE_MAPS)
      this.autocomplete = new window.google.maps.places.Autocomplete(this.$refs.autocomplete, { types: ['address'] })
    } catch (err) {
      log.warning("Failed to wire up Google places autocomplete in BorrowerAttribute", { err })
      return
    }
    this.autocomplete.addListener('place_changed', this.onPlaceChanged)
  },
  computed: {
    errorText() {
      return ''
    },
    invalid() {
      return this.v$.$invalid
    },
  },
  methods: {
    onChange() {
      const value = {
        street: this.street,
        city: this.city,
        state: this.state,
        zip: this.zip,
      }
      if (!Object.values(value).every(x => !!x)) {
        return
      }
      this.$emit('answer', { value, isInvalid: this.v$.$invalid })
    },
    onFocus() {
      this.$emit('focus')
    },
    onAutoComplete() {
      this.onChange()
    },
    onPlaceChanged() {
      let place = this.autocomplete.getPlace()

      if (!place || !place.address_components) return

      let addressComponents = {
        street_number: 'short_name',
        route: 'long_name',
        locality: 'long_name',
        administrative_area_level_1: 'short_name',
        postal_code: 'short_name'
      }

      let returnData = {}

      if (place.address_components !== 'undefined') {
        for (let i = 0; i < place.address_components.length; i++) {
          let addressType = place.address_components[i].types[0]
          if (addressComponents[addressType]) {
            returnData[addressType] = place.address_components[i][addressComponents[addressType]]
          }
        }
      }

      if (returnData.street_number && returnData.route) {
        this.street = `${returnData.street_number} ${returnData.route}`
      } else if (!returnData.street_number && returnData.route) {
        this.street = returnData.route
      }

      this.city = returnData.locality
      this.zip = returnData.postal_code
      if (place.formatted_address.includes('Puerto Rico')) {
        returnData.administrative_area_level_1 = 'PR'
      }
      this.state = returnData.administrative_area_level_1

      this.onChange()
    },
  },
}
</script>

<style lang="scss" scoped>

.form-select.no-value-select {
  color: $type-400;
}

.ba-address-container {
  > *:not(:last-child) {
    margin-bottom: 24px;
  }
}

</style>
